import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/pawkt/PageHeader";
import { Loading } from "@/components/pawkt/Empty";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2, Upload, RefreshCw, FileText, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const FIELDS = [
  ["legal_name", "Legal / registered name"], ["address", "Address"], ["city", "City"],
  ["state", "State"], ["pin", "PIN"], ["phone", "Phone"], ["email", "Email"],
  ["website", "Website"], ["gstin", "GSTIN"], ["pan", "PAN"],
  ["invoice_prefix", "Invoice prefix (e.g. INV)"], ["number_format", "Number format"],
];
const hasPerm = (u) => u?.role === "owner" || u?.role === "admin" || (u?.effective_permissions || u?.permissions || []).includes("billing.edit") || (u?.permissions || []).includes("*");

export default function InvoiceSettings() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [branchId, setBranchId] = useState("");
  const [form, setForm] = useState({});
  const [nonce, setNonce] = useState(0);
  const fileRef = useRef(null);
  const canEdit = hasPerm(user);

  const { data: branches } = useQuery({ queryKey: ["branches"], queryFn: async () => (await api.get("/branches")).data });
  const { data: profile, isLoading } = useQuery({ queryKey: ["invoice-profile", branchId], queryFn: async () => (await api.get(`/billing/invoice-profile?branch_id=${branchId}`)).data });
  const { data: readiness } = useQuery({ queryKey: ["invoice-readiness", branchId], queryFn: async () => (await api.get(`/billing/invoice-readiness?branch_id=${branchId}`)).data });
  useEffect(() => { if (profile) setForm(profile); }, [profile]);

  const save = useMutation({
    mutationFn: (b) => api.put("/billing/invoice-profile", { ...b, branch_id: branchId }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["invoice-profile"] }); qc.invalidateQueries({ queryKey: ["invoice-readiness"] }); setNonce((n) => n + 1); toast.success("Invoice settings saved"); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const uploadLogo = async (f) => {
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      await api.post(`/billing/invoice-profile/logo?branch_id=${branchId}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      qc.invalidateQueries({ queryKey: ["invoice-profile"] }); setNonce((n) => n + 1); toast.success("Logo updated");
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (isLoading) return <Loading />;
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const previewUrl = `${api.defaults.baseURL}/billing/invoice-preview?branch_id=${branchId}&v=${nonce}`;
  const ready = readiness;

  return (
    <div data-testid="invoice-settings-page">
      <PageHeader title="Invoice Settings" subtitle="Your business identity on every invoice — logo, GSTIN, numbering, terms" />
      {ready && (
        <div data-testid="invoice-readiness-banner" className={`mb-4 flex flex-wrap items-center gap-2 rounded-lg border p-3 text-sm ${ready.status === "READY" ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-amber-50 border-amber-200 text-amber-700"}`}>
          {ready.status === "READY"
            ? <><CheckCircle2 className="h-4 w-4" /> <b>Invoice setup: READY</b> — you can generate invoices.</>
            : <><AlertTriangle className="h-4 w-4" /> <b>Invoice setup: ACTION REQUIRED</b> — missing: {(ready.missing_fields || []).map((f) => f).join(", ")}. Complete the required fields below to start generating invoices.</>}
        </div>
      )}
      {!canEdit && <div className="mb-4 flex items-center gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-700"><AlertTriangle className="h-4 w-4" /> You can view invoice settings but need the billing.edit permission to change them.</div>}

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <h3 className="font-display text-lg font-semibold text-slate-900 flex items-center gap-2 mb-4"><Building2 className="h-5 w-5 text-slate-400" /> Business identity</h3>

          {(branches?.length > 1) && (
            <div className="mb-4">
              <Label className="text-xs text-slate-500">Identity for</Label>
              <select data-testid="invoice-settings-branch" value={branchId} onChange={(e) => setBranchId(e.target.value)} className="mt-1 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm">
                <option value="">Whole business (tenant default)</option>
                {branches.map((b) => <option key={b.id} value={b.id}>{`${b.name} (branch override)`}</option>)}
              </select>
              <p className="mt-1 text-xs text-slate-400">Unset branch fields fall back to the tenant identity.</p>
            </div>
          )}

          <div className="flex items-center gap-3 mb-4">
            {form.has_logo ? <img src={`${api.defaults.baseURL}/billing/logo/${form.logo_asset_id}`} alt="logo" className="max-h-[72px] max-w-[200px] h-auto w-auto object-contain rounded border border-slate-200 bg-white p-1" /> : <div className="grid h-12 w-12 place-items-center rounded border border-dashed border-slate-300 text-slate-300"><FileText className="h-5 w-5" /></div>}
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="invoice-logo-input" onChange={(e) => uploadLogo(e.target.files[0])} />
            <Button variant="outline" size="sm" disabled={!canEdit} className="gap-1.5" data-testid="invoice-logo-btn" onClick={() => fileRef.current?.click()}><Upload className="h-4 w-4" /> Upload logo</Button>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {FIELDS.map(([k, label]) => (
              <div key={k} className={k === "address" ? "col-span-2" : ""}>
                <Label className="text-xs text-slate-500">{label}</Label>
                <Input value={form[k] || ""} onChange={set(k)} disabled={!canEdit} data-testid={`invoice-field-${k}`} className="mt-1 bg-white" placeholder={k === "number_format" ? "INV-2026-{seq:04d}" : ""} />
              </div>
            ))}
            <div className="col-span-2 flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
              <input type="checkbox" id="gstreg" checked={!!form.gst_registered} disabled={!canEdit} data-testid="invoice-field-gst_registered" onChange={(e) => setForm((s) => ({ ...s, gst_registered: e.target.checked }))} className="h-4 w-4" />
              <Label htmlFor="gstreg" className="text-sm text-slate-700">This business is GST-registered (GSTIN becomes required)</Label>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Tax display</Label>
              <select value={form.tax_display || "split"} onChange={set("tax_display")} disabled={!canEdit} data-testid="invoice-field-tax_display" className="mt-1 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm">
                <option value="split">CGST + SGST (intra-state)</option>
                <option value="igst">IGST (inter-state)</option>
                <option value="none">Single tax line</option>
              </select>
            </div>
            {["terms", "payment_instructions", "footer"].map((k) => (
              <div key={k} className="col-span-2">
                <Label className="text-xs text-slate-500 capitalize">{k.replace("_", " ")}</Label>
                <textarea value={form[k] || ""} onChange={set(k)} disabled={!canEdit} data-testid={`invoice-field-${k}`} rows={2} className="mt-1 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm" />
              </div>
            ))}
          </div>
          <p className="mt-3 text-xs text-slate-400">Numbering is always server-generated and sequential. Tokens: {"{prefix} {yyyy} {seq:04d}"}. GST split is a display of the invoice's canonical tax total.</p>
          <Button onClick={() => save.mutate(form)} disabled={!canEdit || save.isPending} data-testid="invoice-settings-save" className="mt-4 bg-slate-900 text-white">{save.isPending ? "Saving…" : "Save settings"}</Button>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display text-lg font-semibold text-slate-900 flex items-center gap-2"><FileText className="h-5 w-5 text-slate-400" /> Live preview</h3>
            <Button variant="outline" size="sm" className="gap-1.5" data-testid="invoice-preview-refresh" onClick={() => setNonce((n) => n + 1)}><RefreshCw className="h-4 w-4" /> Refresh</Button>
          </div>
          <p className="text-xs text-slate-400 mb-2">Sample data only — this never creates a real invoice.</p>
          <iframe key={nonce} title="invoice-preview" data-testid="invoice-preview-frame" src={previewUrl} className="w-full rounded-lg border border-slate-200" style={{ height: "560px" }} />
        </div>
      </div>
    </div>
  );
}
